import { CognitoUserAmplify } from "@aws-amplify/ui";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { getUserRole } from "../utils/auth.util";
import { Environment } from "../env/Environment";
import { awsRum } from "../rum/cloudwatchRum";

const UserSettingsSchema = z.object({
  sandboxMode: z.boolean().optional(),
});

export type UserSettings = z.infer<typeof UserSettingsSchema>;

function parseUserSettings(settings: unknown): UserSettings {
  if (!settings) return { sandboxMode: true };
  try {
    return UserSettingsSchema.parse(settings);
  } catch (error) {
    awsRum?.recordError(error instanceof Error ? error : new Error("Failed to parse user settings"));
    return { sandboxMode: true };
  }
}

export interface UseAuthUserResult extends Pick<ReturnType<typeof useAuthenticator>, "signOut"> {
  cognitoUser: CognitoUserAmplify;
  dashboardUser?: GraphQL.DashboardUserCurrentQuery["dashboardUserCurrent"];
  isDashboardUserLoading: boolean;
  isDashboardUserFetching: boolean;
  id?: string;
  role?: GraphQL.DashboardUserRole;
  /**
   * @deprecated
   * Use 'is' instead, it works better with the unit tests
   */
  hasRole: (userRole: GraphQL.DashboardUserRole) => boolean;
  is: {
    USER: boolean;
    SUPER_ADMIN: boolean;
    CUSTOMER_ADMIN: boolean;
  };
  isSandbox: boolean;
  setIsSandbox: (value: boolean) => void;
}

export const useAuthUser = (): UseAuthUserResult => {
  const { user: cognitoUser, signOut } = useAuthenticator((context) => [context.user]);
  const queryClient = useQueryClient();
  const {
    data,
    isLoading: isDashboardUserLoading,
    isFetching: isDashboardUserFetching,
  } = GraphQL.useDashboardUserCurrentQuery(undefined, {
    enabled: !!cognitoUser,
    staleTime: Infinity,
  });
  const dashboardUser = data?.dashboardUserCurrent;

  const userSettings = parseUserSettings(dashboardUser?.settings);

  // Respect Environment to prevent "sandbox in a sandbox"
  const isSandbox = !Environment.isSandbox && (userSettings.sandboxMode ?? true);

  const setIsSandbox = (value: boolean) => {
    const newSettings: UserSettings = {
      ...userSettings,
      sandboxMode: value,
    };

    updateSettingsMutation.mutate({
      settings: JSON.stringify(newSettings),
    });
  };
  const updateSettingsMutation = GraphQL.useUpdateDashboardUserSettingsMutation({
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["DashboardUserCurrent"] });
      const previousData = queryClient.getQueryData<GraphQL.DashboardUserCurrentQuery>(["DashboardUserCurrent"]);
      queryClient.setQueryData<GraphQL.DashboardUserCurrentQuery>(["DashboardUserCurrent"], (oldData) => {
        if (!oldData) return oldData;
        const newSettings = JSON.parse(variables.settings);

        return {
          ...oldData,
          dashboardUserCurrent: {
            ...oldData.dashboardUserCurrent,
            settings: newSettings,
          },
        };
      });
      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(["DashboardUserCurrent"], context.previousData);
      }
    },
  });

  const id = dashboardUser?.customerPublicId ?? undefined;

  const role = React.useMemo(() => getUserRole(cognitoUser), [cognitoUser]);

  const hasRole = React.useCallback((userRole: GraphQL.DashboardUserRole) => userRole === role, [role]);

  const is: UseAuthUserResult["is"] = React.useMemo(
    () => ({
      USER: hasRole(GraphQL.DashboardUserRole.User),
      SUPER_ADMIN: hasRole(GraphQL.DashboardUserRole.SuperAdmin),
      CUSTOMER_ADMIN: hasRole(GraphQL.DashboardUserRole.CustomerAdmin),
    }),
    [hasRole]
  );

  return {
    cognitoUser,
    dashboardUser,
    isDashboardUserLoading,
    isDashboardUserFetching,
    id,
    role,
    hasRole,
    is,
    signOut,
    isSandbox,
    setIsSandbox,
  };
};
